import * as React from "react";
import { SimpleBreadcrumbs } from "../../components/navigation/simpleBreadcrumbs";
import StandardLayout from "../../layouts/standard";
import MyInfo from "./accessDetail/myInfoTab";
import Bios from "./accessDetail/biosTab";
import Assignments from "./accessDetail/assignmentsTab";
import MyFilesTab from "./accessDetail/myFilesTab/myFilesTab";
import Applications from "./accessDetail/applicationsTab";
import Notifications from "./accessDetail/notificationsTab";
import { Alert, Box, Link, Typography } from "@mui/material";
import { UserClaimsService, eGroupIds } from "../../services/currentUserService/currentUserService";
import ResourceProductSalesPanel from "../resources/resourceDetail/participants/productsales/resourceProductSalesPanel";
import DetailPage from "../../components/common/detailPage/detailPage";
import MyAttributesTabPanel from "./accessDetail/myAttributesTab";
import ResourceEventCalendar from "../../components/shared/resourceEventCalendar/resourceEventCalendar";

interface MyAccessProps {}

const MyAccess: React.FC<MyAccessProps> = () => {
    const claimsService = new UserClaimsService();
    const [name] = React.useState<string>(
        claimsService.GetCurrentUserName() ? claimsService.GetCurrentUserName() : "Unknown User"
    );
    const [email] = React.useState<string>(claimsService.GetCurrentUserEmail());

    const fullTablist: [string, number, React.ReactNode][] = [
        ["My Files", 1, <MyFilesTab clientAdmin={claimsService.IsUserInGroups([eGroupIds.eClientAdmin])} />],
        ["My Profile", 2, <MyInfo name={name} email={email} />],
        [
            "My Bio",
            3,
            <Bios
                pKey={claimsService.GetCurrentResourceId()}
                email={claimsService.GetCurrentUserEmail()}
            />,
        ],
        [
            "My Attributes",
            4,
            <MyAttributesTabPanel
                readonly={false}
                fromResource={false}
                pkey={claimsService.GetCurrentResourceId()}
                email={claimsService.GetCurrentUserEmail()}
                imKey={claimsService.GetCurrentUserId()}
            />,
        ],
        ["My Assignments", 5, <Assignments />],
        [
            "My Calendar",
            8,
            <ResourceEventCalendar
                imKey={claimsService.GetCurrentUserId()}
                pKey={claimsService.GetCurrentResourceId()}
            />,
        ],
        ["My Emails", 6, <Notifications />],
        ["My Applications", 7, <Applications />],
    ];

    const nwaAdminTablist: [string, number, React.ReactNode][] = [
        ["My Files", 1, <MyFilesTab />],
        ["My Emails", 3, <Notifications />],
        ["My Applications", 2, <Applications />],
    ];

    const clientAdminTabList: [string, number, React.ReactNode][] = [
        ["My Files", 1, <MyFilesTab />],
        ["My Emails", 6, <Notifications />],
        ["My Applications", 2, <Applications />],
    ];


    const clientFacilitatorAndNwaResourceTabList: [string, number, React.ReactNode][] = [
        ["My Files", 1, <MyFilesTab />],
    ];

    const breadcrumbs = (
        <SimpleBreadcrumbs>
            <Typography variant="h6" color="text.primary">
                {`My Access / ` + name}
            </Typography>
        </SimpleBreadcrumbs>
    );

    const finalTablist = claimsService.IsUserInGroups([eGroupIds.eNWAAdmin])
        ? nwaAdminTablist
        : claimsService.IsUserInGroups([eGroupIds.eClientFacilitators, eGroupIds.eNWAResource])
        ? clientFacilitatorAndNwaResourceTabList
        : claimsService.IsUserInGroups([eGroupIds.eClientAdmin])
        ? clientAdminTabList
        : fullTablist;

    const bypassViewMyAccess = claimsService.IsUserInGroups([
        eGroupIds.eClientAdmin,
    ]);

    const isClientAdmin = claimsService.IsUserInGroups([eGroupIds.eClientAdmin]);

    const clientAdminBanner = isClientAdmin ? (
        <Box
            sx={{
                width: "100%",
                border: "1px solid #E0E0E0",
                borderRadius: "4px",
                overflow: "hidden",
                display: "block",
                position: "relative",
            }}
        >
            <Box
                component="img"
                src="https://coreprodeuscclstg.blob.core.windows.net/public-web-assets/access-assets/images/HomePageTitleBar.jpg"
                alt="My Access Title Bar"
                sx={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                }}
            />
        </Box>
    ) : undefined;

    if (claimsService.CanUserViewMyAccess() || bypassViewMyAccess) {
        return (
            <DetailPage
                breadcrumbs={breadcrumbs}
                descriptor="My Access"
                tablist={finalTablist}
                activeTab={1}
                identifier="my-access"
                headerContent={clientAdminBanner}
            ></DetailPage>
        );
    } else if (claimsService.IsUserInGroups([eGroupIds.eClientFacilitators])) {
        return (
            <StandardLayout breadcrumbs={breadcrumbs}>
                <ResourceProductSalesPanel
                    email={claimsService.GetCurrentUserEmail()}
                    isUserDetail={false}
                />
            </StandardLayout>
        );
    } else if (claimsService.IsUserInGroups([eGroupIds.eNWAResource])) {
        return (
            <StandardLayout breadcrumbs={breadcrumbs}>
                <MyFilesTab />
            </StandardLayout>
        );
    } else {
        return (
            <StandardLayout breadcrumbs={breadcrumbs}>
                <Alert severity="info">
                    Users must have a Bookable Resource Record in D365 PSA in order to use the My
                    Access module. Our records indicate that you do not have a Bookable Resource
                    Record in D365 PSA. A User Account Location Management Form (
                    <Link
                        href={"docs/UserAccountLocationManagementForm.docx"}
                        download={"UserAccountLocationManagementForm.docx"}
                    >
                        link
                    </Link>
                    ) will need to be filled out by your supervisor and submitted with a HTS ticket
                    assigned to <b>D365 - Access Request</b>. If you are an Independent Contractor
                    or Associate please reach out to your contact at CCL with this information.
                </Alert>
            </StandardLayout>
        );
    }
};

export default MyAccess;
